import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const initialState = {
  groupsList: [],
  currentGroup: null,
  isLoading: false,
  selectedTab: 1,
  reportAnalysisData: null,
  reportAnalysisLoading: false,
};

export const createNewGroup = createAsyncThunk(
  "groups/createNewGroup",
  async ({ group, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/groups?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, group, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const editGroup = createAsyncThunk(
  "groups/editGroup",
  async ({ group, group_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/groups/${group_id}?account_id=${selectedAccount}`;

    try {
      const response = await axios.put(url, group, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteGroup = createAsyncThunk(
  "groups/deleteGroup",
  async ({ groupId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/groups/${groupId}?account_id=${selectedAccount}`;

    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Group deleted successfully");
      return { groupId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const archiveGroup = createAsyncThunk(
  "groups/archiveGroup",
  async ({ groupId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/groups/${groupId}/archive?account_id=${selectedAccount}`;
    try {
      await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      toast.success("Group archived successfully");
      return { groupId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to archive group" });
    }
  }
);

export const unarchiveGroup = createAsyncThunk(
  "groups/unarchiveGroup",
  async ({ groupId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/groups/${groupId}/unarchive?account_id=${selectedAccount}`;
    try {
      await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      toast.success("Group unarchived successfully");
      return { groupId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to unarchive group" });
    }
  }
);
export const getGroup = createAsyncThunk(
  "groups/getGroup",
  async ({ groupId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/groups/${groupId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllGroups = createAsyncThunk(
  "groups/getAllGroups",
  async (
    { searchQuery = "", page, rowsPerPage, selectedAccount, archive = false },
    { rejectWithValue }
  ) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/groups?q=${encodeURIComponent(searchQuery)}&page=${page + 1
      }&per_page=${rowsPerPage}&account_id=${selectedAccount}&archive=${archive}`;

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// New API for Group Report Analysis
export const fetchGroupReportAnalysis = createAsyncThunk(
  "groups/fetchGroupReportAnalysis",
  async ({ workerIds }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/groups/report_analysis`;

    try {
      const response = await axios.post(url, {
        worker_ids: workerIds
      }, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const groupSlice = createSlice({
  name: "group",
  initialState,
  reducers: {
    setSelectedTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    clearReportAnalysisData: (state) => {
      state.reportAnalysisData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllGroups.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllGroups.fulfilled, (state, action) => {
        state.groupsList = action.payload.groups;
        state.isLoading = false;
        state.totalGroupsCount = action.payload.meta.total_count;
      })
      .addCase(getAllGroups.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.groupsList = state.groupsList.filter(
          (group) => group.id !== action.payload.groupId
        );
      })
      .addCase(archiveGroup.fulfilled, (state, action) => {
        state.groupsList = state.groupsList.filter(
          (group) => group.id !== action.payload.groupId
        );
        state.isLoading = false;
      })
      .addCase(unarchiveGroup.fulfilled, (state, action) => {
        state.groupsList = state.groupsList.filter(
          (group) => group.id !== action.payload.groupId
        );
        state.isLoading = false;
      })
      .addCase(getGroup.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getGroup.fulfilled, (state, action) => {
        state.currentGroup = action.payload;
        state.isLoading = false;
      })
      .addCase(getGroup.rejected, (state) => {
        state.isLoading = false;
      })
      // Group Report Analysis
      .addCase(fetchGroupReportAnalysis.pending, (state) => {
        state.reportAnalysisLoading = true;
      })
      .addCase(fetchGroupReportAnalysis.fulfilled, (state, action) => {
        state.reportAnalysisLoading = false;
        state.reportAnalysisData = action.payload;
      })
      .addCase(fetchGroupReportAnalysis.rejected, (state, action) => {
        state.reportAnalysisLoading = false;
        toast.error("Failed to fetch report analysis data");
      });
  },
});
export const { setSelectedTab, clearReportAnalysisData } = groupSlice.actions;

export default groupSlice.reducer;
